import { Menu, Modal } from '@mantine/core'
import PrimaryDropDown from 'app/views/components/DropDown/DropDown'
import { useDispatch, useSelector } from 'react-redux'
import { LoanParams } from 'app/models/loan-params'
import {
  approveLoan,
  submitLoan,
  deleteLoan,
  rejectLoan,
  deleteLoanAmendment,
  submitLoanAmendment,
  rejectLoanAmendment,
  approveLoanAmendment,
} from './loan-daml-actions'
import { useState } from 'react'
import HistoryForm from './history/history'
import RateSettingForm from '../rate-settings/rate-setting-form'
import PrincipalPaymentModal from '../principal-payment/principal-payment-modal'
import { useNavigate } from 'react-router-dom'
import { UpdatedLoanScreen } from './loan-stepper'
import { loadOneFacility } from 'app/state/ducks/facilities/thunks'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { loadIndexRateOptions } from 'app/state/ducks/index-rate-options/thunks'
import { LOAD_FAILED } from 'app/state/utils/load-result'
import { ErrorNotification } from 'app/views/components/notifications/notification'
import RolloverFormLogic from '../rollovers/rollover-form-logic'
import IncreaseFormLogic from '../increases/increase-form-logic'
import ProcessPIKForm from '../payment-in-kind/pik-form'
import { LoanRolloverParams } from 'app/models/loan-rollover-params'
import { LoanIncreaseParams } from 'app/models/loan-increase-params'
import IonIcon from '@reacticons/ionicons'
import ConversionFormLogic from '../conversion/conversion-form-logic'
import { LoanConversionParams } from 'app/models/loan-conversion-params'
import { PrincipalPaymentParams } from 'app/models/principal-payments-params'
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation'

/**
 * LoanActions Component
 * 
 * Renders a dropdown menu with contextual actions for loan management operations.
 * Actions displayed depend on loan status, type (amendment/edit), and user permissions.
 * 
 * For detailed documentation, architecture, and usage examples, see:
 * https://github.com/rplendos/right-pedal/wiki/Loan-Actions-Component
 */

type ModalType =
  | 'edit'
  | 'amendment'
  | 'loanEdit'
  | 'rollover'
  | 'increase'
  | 'conversion'
  | 'payment'
  | 'rateSetting'
  | 'pik'
  | 'history'
  | 'delete'

/**
 * Defines a single loan action that appears in the dropdown menu
 * 
 * @property label - The static text displayed for the action (e.g., "Edit", "Submit For Approval")
 * @property icon - The Ionicon name to display next to the action (e.g., "settings-outline", "trash-outline")
 * @property onClick - Function called when the action is clicked (handles the action's behavior)
 * @property show - Boolean that determines if this action should be visible for a given loan displayed in the Loans Page table (for example, "Submit For Approval" is only shown for loans in "Draft" status)
 * @property isDanger - Optional flag to mark dangerous actions like "Delete" and "Reject" (displays in red in "Danger zone" section)
 * @property getDynamicLabel - Optional function to override the static label with dynamic text
 *                            (e.g., "Process Rollover" vs "Edit Rollover" based on state)
 */
interface LoanAction {
  label: string
  icon: string
  onClick: () => void
  show: boolean
  isDanger?: boolean
  getDynamicLabel?: () => string
}

type Props = {
  item: LoanParams
  setOpenEdit?: React.Dispatch<React.SetStateAction<boolean>>
  setLoan?: React.Dispatch<React.SetStateAction<any>>
  fromOutside: boolean
  rollover?: LoanRolloverParams | undefined
  increase?: LoanIncreaseParams | undefined
  conversion?: LoanConversionParams
  payment?: PrincipalPaymentParams
  /** The loan's facility `pikOption` when the caller knows it (the servicing feed row carries it); otherwise the single-facility slice is used. */
  pikOption?: string | null
}

export default function LoanActions({ item, setOpenEdit, setLoan, rollover, increase, fromOutside, conversion, payment, pikOption }: Props) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const facility = useSelector(getFacility)
  const allIndexes = useSelector(getIndexRateOptions)

  // Computed values
  const isTermIndex = allIndexes.find(index => index.id === item.indexOption?.id)?.indexType === "TermIndex"
  const isItemAmendment = item?.amendmentType !== undefined && item?.amendmentType !== null
  const isItemEdit = item?.amendmentType === 'LoanEdit'
  const isItemRateAmendment = item?.amendmentType === 'LoanAmendment'
  // null means the caller KNOWS the facility is not on its grid; only an
  // absent prop falls back to the single-facility slice
  const facilityPikOption = pikOption !== undefined ? pikOption : facility?.pikOption

  // Term-index gating reads the index rate options, loaded here on open when
  // the store is cold (the Servicing and My Work pages load no list). Until
  // they land an approved loan's menu shows only a loading line: listing the
  // other items first and adding Rollover / Increase / Conversion after would
  // push Process Payment down under the pointer, and a quick click would open
  // the wrong form (LOS-7218). If the load fails, the menu shows without the
  // term-dependent items and says so; the next open retries.
  const indexesLoaded = allIndexes.length > 0
  // 'done' after a load that succeeded, even with no option at all: the
  // menu must never wait for a list that came back empty. Back to 'pending'
  // on every open that loads, so a retry after a failure waits too.
  const [indexLoad, setIndexLoad] = useState<'pending' | 'failed' | 'done'>('pending')
  const ensureIndexRateOptions = async () => {
    if (indexesLoaded) return
    setIndexLoad('pending')
    const result = await dispatch(loadIndexRateOptions() as any)
    if (result === LOAD_FAILED) {
      setIndexLoad('failed')
      ErrorNotification({
        title: 'Could not load index rate options',
        message: 'Rollover, Increase and Conversion are unavailable. Reopen the menu to try again.',
      })
    } else {
      setIndexLoad('done')
    }
  }
  const waitingForIndexes =
    item?.status?.toLowerCase() === 'approved' && !indexesLoaded && indexLoad === 'pending'

  // State
  const [openModals, setOpenModals] = useState<Record<ModalType, boolean>>({
    edit: false,
    amendment: false,
    loanEdit: false,
    rollover: false,
    increase: false,
    conversion: false,
    payment: false,
    rateSetting: false,
    pik: false,
    history: false,
    delete: false
  })

  const [loanLocal, setLoanLocal] = useState<LoanParams>()

  // Single modal handler - this simplifies modal management significantly
  const handleModal = (modalType: ModalType, setupFn?: () => void) => {
    // Run any setup (like loading facility)
    setupFn?.()

    // Handle special case for fromOutside edit
    if (fromOutside && setLoan && setOpenEdit && modalType === 'edit') {
      setLoan(item)
      setOpenEdit(true)
      return
    }

    // Set loan local and open modal
    setLoanLocal(item)
    setOpenModals(prev => ({ ...prev, [modalType]: true }))
  }

  const closeModal = (modalType: ModalType) => {
    setOpenModals(prev => ({ ...prev, [modalType]: false }))
  }

  // Action handler functions - simplified with conditional logic
  const handleEdit = () => {
    const setupFacility = () => dispatch(loadOneFacility(item.facilityId.id, 'Approved'))

    if (isItemEdit) {
      handleModal('loanEdit', setupFacility)
    } else if (isItemRateAmendment) {
      handleModal('amendment', setupFacility)
    } else {
      handleModal('edit', setupFacility)
    }
  }

  const handleSubmit = async () => {
    if (isItemAmendment) {
      await submitLoanAmendment(item, dispatch)
    } else {
      await submitLoan(item, dispatch)
    }
  }

  const handleApprove = async () => {
    if (isItemAmendment) {
      await approveLoanAmendment(item, dispatch)
    } else {
      await approveLoan(item, dispatch)
    }
  }

  const handleReject = async () => {
    if (isItemAmendment) {
      await rejectLoanAmendment(item, dispatch)
    } else {
      await rejectLoan(item, dispatch)
    }
  }

  const handleDelete = async () => {
    if (isItemAmendment) {
      await deleteLoanAmendment(item, dispatch)
    } else {
      await deleteLoan(item, dispatch)
    }
    closeModal('delete')
  }

  // Action definitions - much cleaner and easier to follow
  const actions: LoanAction[] = [
    // Navigation
    {
      label: 'Lenders Position',
      icon: 'settings-outline',
      onClick: () => navigate(`/dealmanagement/loans/${item.id}/${item.status}/lenders`),
      show: ['locked', 'approved'].includes(item?.status?.toLowerCase() || '')
    },

    // Draft Actions
    {
      label: 'Edit',
      icon: 'settings-outline',
      onClick: handleEdit,
      show: item?.status?.toLowerCase() === 'draft'
    },
    {
      label: 'Submit For Approval',
      icon: isItemAmendment ? 'swap-horizontal-outline' : 'open-outline',
      onClick: handleSubmit,
      show: item?.status?.toLowerCase() === 'draft'
    },

    // Submitted Actions
    {
      label: 'Approve',
      icon: 'swap-horizontal-outline',
      onClick: handleApprove,
      show: item?.status?.toLowerCase() === 'submitted'
    },

    // Approved Actions
    {
      label: 'Amendment',
      icon: 'settings-outline',
      onClick: () => handleModal('amendment', () => dispatch(loadOneFacility(item.facilityId.id, 'Approved'))),
      show: item?.status?.toLowerCase() === 'approved'
    },
    {
      label: 'Edit',
      icon: 'settings-outline',
      onClick: () => handleModal('loanEdit', () => dispatch(loadOneFacility(item.facilityId.id, 'Approved'))),
      show: item?.status?.toLowerCase() === 'approved'
    },
    {
      label: 'Add Rates',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('rateSetting'),
      show: item?.status?.toLowerCase() === 'approved' && (!item?.interestBaseRate || item?.interestBaseRate === 0.0)
    },
    {
      label: 'Process PIK',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('pik'),
      show: item?.status?.toLowerCase() === 'approved' && facilityPikOption !== 'No_PIK'
    },

    // Term Index Actions
    {
      label: 'Process Rollover',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('rollover'),
      show: item?.status?.toLowerCase() === 'approved' && indexesLoaded && isTermIndex,
      getDynamicLabel: () => rollover ? 'Edit Rollover' : 'Process Rollover'
    },

    // Non-Term Index Actions  
    {
      label: 'Process Increase',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('increase'),
      show: item?.status?.toLowerCase() === 'approved' && indexesLoaded && !isTermIndex,
      getDynamicLabel: () => increase ? 'Edit Increase' : 'Process Increase'
    },
    {
      label: 'Process Conversion',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('conversion'),
      show: item?.status?.toLowerCase() === 'approved' && indexesLoaded && !isTermIndex,
      getDynamicLabel: () => conversion ? 'Edit Conversion' : 'Process Conversion'
    },
    {
      label: 'Process Payment',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('payment'),
      show: item?.status?.toLowerCase() === 'approved',
      getDynamicLabel: () => payment ? 'Edit Payment' : 'Process Payment'
    },

    // History
    {
      label: 'History',
      icon: 'swap-horizontal-outline',
      onClick: () => handleModal('history'),
      show: ['locked', 'approved'].includes(item?.status?.toLowerCase() || '')
    },

    // Danger Zone
    {
      label: 'Delete',
      icon: 'trash-outline',
      onClick: () => handleModal('delete'),
      show: (() => {
        if (isItemAmendment) {
          return item?.status?.toLowerCase() === 'draft'
        }
        return ['draft', 'approved'].includes(item?.status?.toLowerCase() || '')
      })(),
      isDanger: true
    },
    {
      label: 'Reject',
      icon: 'swap-horizontal-outline',
      onClick: handleReject,
      show: item?.status?.toLowerCase() === 'submitted',
      isDanger: true
    }
  ]

  // Filter visible actions
  const visibleActions = actions.filter(action => action.show)
  const primaryActions = visibleActions.filter(action => !action.isDanger)
  const dangerActions = visibleActions.filter(action => action.isDanger)

  // Get delete modal message
  const getDeleteMessage = () => {
    if (isItemEdit) return "Delete This Draft Loan Edit?"
    if (isItemRateAmendment) return "Delete This Draft Loan Amendment?"
    return "Delete This Loan?"
  }

  return (
    <>
      <Menu shadow="md" width={200} position="bottom" withinPortal onOpen={ensureIndexRateOptions}>
        <Menu.Target>
          <div style={{ width: 'fit-content' }}>
            <PrimaryDropDown>Actions</PrimaryDropDown>
          </div>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Loan Actions</Menu.Label>
          {waitingForIndexes ? (
            <Menu.Item disabled>Loading actions…</Menu.Item>
          ) : primaryActions.map((action, index) => (
            <Menu.Item
              key={index}
              onClick={action.onClick}
              icon={<IonIcon className="action-ion-icon" name={action.icon as any} />}
            >
              {action.getDynamicLabel ? action.getDynamicLabel() : action.label}
            </Menu.Item>
          ))}

          {!waitingForIndexes && dangerActions.length > 0 && (
            <>
              <Menu.Divider />
              <Menu.Label>Danger zone</Menu.Label>
              {dangerActions.map((action, index) => (
                <Menu.Item
                  key={index}
                  color="red"
                  onClick={action.onClick}
                  icon={<IonIcon className="action-ion-icon" name={action.icon as any} />}
                >
                  {action.getDynamicLabel ? action.getDynamicLabel() : action.label}
                </Menu.Item>
              ))}
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      {/* Edit Modal */}
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.edit}
        onClose={() => closeModal('edit')}
        centered={true}
        size="77%"
      >
        <UpdatedLoanScreen
          facility={facility}
          loan={loanLocal}
          step={0}
          onClose={() => closeModal('edit')}
        />
      </Modal>

      {/* Amendment Modal */}
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.amendment}
        onClose={() => closeModal('amendment')}
        centered={true}
        size="77%"
      >
        <UpdatedLoanScreen
          facility={facility}
          loan={loanLocal}
          step={0}
          isAmendment={true}
          onClose={() => closeModal('amendment')}
        />
      </Modal>

      {/* Loan Edit Modal */}
      <Modal
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.loanEdit}
        onClose={() => closeModal('loanEdit')}
        centered={true}
        size="77%"
      >
        <UpdatedLoanScreen
          facility={facility}
          loan={loanLocal}
          step={0}
          isEdit={true}
          onClose={() => closeModal('loanEdit')}
        />
      </Modal>

      {/* Other Modals */}
      <Modal
        size="50%"
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.rollover}
        onClose={() => closeModal('rollover')}
      >
        {loanLocal && (
          <RolloverFormLogic loan={loanLocal} close={() => closeModal('rollover')} />
        )}
      </Modal>

      <Modal
        size="50%"
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.increase}
        onClose={() => closeModal('increase')}
      >
        {loanLocal && (
          <IncreaseFormLogic loan={loanLocal} close={() => closeModal('increase')} />
        )}
      </Modal>

      <Modal
        size="50%"
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.conversion}
        onClose={() => closeModal('conversion')}
      >
        {loanLocal && (
          <ConversionFormLogic loan={loanLocal} close={() => closeModal('conversion')} />
        )}
      </Modal>

      <Modal
        size="50%"
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.pik}
        onClose={() => closeModal('pik')}
      >
        {loanLocal && (
          <ProcessPIKForm loan={loanLocal} close={() => closeModal('pik')} />
        )}
      </Modal>

      {loanLocal && (
        <PrincipalPaymentModal
          loan={loanLocal}
          setOpen={(open: boolean) => setOpenModals(prev => ({ ...prev, payment: open }))}
          open={openModals.payment}
        />
      )}

      <Modal
        size="50%"
        className="modal-body modal-body-template fit-content-modal"
        opened={openModals.rateSetting}
        onClose={() => closeModal('rateSetting')}
      >
        {loanLocal && (
          <RateSettingForm
            loan={loanLocal}
            close={() => closeModal('rateSetting')}
          />
        )}
      </Modal>

      <Modal
        size="90%"
        opened={openModals.history}
        onClose={() => closeModal('history')}
      >
        {loanLocal && (
          <HistoryForm
            loan={loanLocal ?? ''}
            close={() => closeModal('history')}
          />
        )}
      </Modal>

      {openModals.delete && (
        <DeleteConfirmation
          onConfirm={handleDelete}
          onCancel={() => closeModal('delete')}
          message={getDeleteMessage()}
        />
      )}
    </>
  )
}
